import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

/** One override row (history-preserving — includes revoked / expired rows). */
export const InventoryPolicyOverrideRowSchema = z.object({
  id: z.string().uuid(),
  inventoryPolicyId: z.string().uuid(),
  minQuantity: z.number().nullable(),
  maxQuantity: z.number().nullable(),
  autoTransferEligible: z.boolean().nullable(),
  autoBuyEligible: z.boolean().nullable(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  deactivatedAt: z.string().nullable(),
  /** True iff active right now (not deactivated, within [startsAt, endsAt)). */
  isActive: z.boolean(),
  createdAt: z.string(),
});

export type InventoryPolicyOverrideRow = z.infer<
  typeof InventoryPolicyOverrideRowSchema
>;

/**
 * Target a policy by its natural key. ProductV3 (`productV3Key`, the SKU) is the
 * authoritative identity; the legacy v2 `productId` + `productVariantId` are the
 * fallback for policies written before the re-key. Every field is optional
 * because which ones exist depends on the row — callers must supply at least one
 * product key, enforced by {@link atLeastOneProductKey}.
 */
const PolicyTargetShape = {
  warehouseId: z.string().uuid(),
  /** ProductV3 key (the SKU). Preferred — the only key that reaches v3-only rows. */
  productV3Key: z.string().min(1).nullable().optional(),
  /** Legacy v2 product id. */
  productId: z.string().uuid().nullable().optional(),
  /**
   * Legacy v2 variant. ProductV3 has no variants, so this is null on every
   * v3-era policy; a null here means "the policy with no variant", not "any".
   */
  productVariantId: z.string().uuid().nullable().optional(),
} as const;

const atLeastOneProductKey = {
  check: (v: { productV3Key?: string | null; productId?: string | null }) =>
    v.productV3Key != null || v.productId != null,
  message: "Supply productV3Key (preferred) or productId.",
} as const;

/** The resolved effective policy (base ⊕ active overlay) for display. */
export const EffectivePolicySchema = z.object({
  policyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  /** ProductV3 key (the SKU) — the policy's real product identity. */
  productV3Key: z.string().nullable(),
  /**
   * Legacy v2 passenger. NULL on a v3-only policy, which is why this is nullable
   * — it used to be a required uuid, and that made every v3 policy unpublishable.
   */
  productId: z.string().uuid().nullable(),
  productVariantId: z.string().uuid().nullable(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  autoTransferEligible: z.boolean(),
  autoBuyEligible: z.boolean(),
  baseMinQuantity: z.number(),
  baseMaxQuantity: z.number(),
  baseAutoTransferEligible: z.boolean(),
  baseAutoBuyEligible: z.boolean(),
  // Forecast-computed levels (read-only; null until the forecast has run).
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  activeOverrideCount: z.number(),
});

export type EffectivePolicy = z.infer<typeof EffectivePolicySchema>;

// ─── Create ─────────────────────────────────────────────────────────────────

export const CreateInventoryPolicyOverrideInputSchema = z
  .object({
    // Target policy by its natural key (matches how the board addresses a line).
    ...PolicyTargetShape,
    // Overridable fields. Omit / null = "this override doesn't touch it".
    minQuantity: z.number().nonnegative().nullable().optional(),
    maxQuantity: z.number().nonnegative().nullable().optional(),
    autoTransferEligible: z.boolean().nullable().optional(),
    autoBuyEligible: z.boolean().nullable().optional(),
    // Window. startsAt defaults to now; endsAt null = permanent.
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    reason: z.string().max(1000).nullable().optional(),
  })
  .refine(atLeastOneProductKey.check, {
    message: atLeastOneProductKey.message,
  })
  .refine(
    (v) =>
      v.minQuantity != null ||
      v.maxQuantity != null ||
      v.autoTransferEligible != null ||
      v.autoBuyEligible != null,
    { message: "An override must set at least one field." },
  )
  .refine(
    (v) =>
      v.minQuantity == null ||
      v.maxQuantity == null ||
      v.maxQuantity >= v.minQuantity,
    {
      message: "maxQuantity must be >= minQuantity.",
    },
  )
  .refine(
    (v) => {
      if (!v.endsAt) return true;
      const start = v.startsAt ? new Date(v.startsAt) : new Date();
      return new Date(v.endsAt).getTime() > start.getTime();
    },
    {
      path: ["endsAt"],
      message: "endsAt must be later than startsAt.",
    },
  );

export const CreateInventoryPolicyOverrideResponseSchema = z.object({
  override: InventoryPolicyOverrideRowSchema,
  effective: EffectivePolicySchema,
});

export type CreateInventoryPolicyOverrideInput = z.infer<
  typeof CreateInventoryPolicyOverrideInputSchema
>;
export type CreateInventoryPolicyOverrideResponse = z.infer<
  typeof CreateInventoryPolicyOverrideResponseSchema
>;

// ─── List by policy ──────────────────────────────────────────────────────────

export const ListInventoryPolicyOverridesInputSchema = z
  .object(PolicyTargetShape)
  .refine(atLeastOneProductKey.check, {
    message: atLeastOneProductKey.message,
  });

export const ListInventoryPolicyOverridesResponseSchema = z.object({
  policyId: z.string().uuid().nullable(),
  effective: EffectivePolicySchema.nullable(),
  overrides: z.array(InventoryPolicyOverrideRowSchema),
});

export type ListInventoryPolicyOverridesInput = z.infer<
  typeof ListInventoryPolicyOverridesInputSchema
>;
export type ListInventoryPolicyOverridesResponse = z.infer<
  typeof ListInventoryPolicyOverridesResponseSchema
>;

// ─── List by product (one row per warehouse×variant policy combo) ────────────

/** One policy combo for a product — effective + base values for the table. */
export const PolicyOverrideComboRowSchema = z.object({
  policyId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  divisionName: z.string().nullable(),
  /** ProductV3 key (the SKU) — echo it back so the row can address its policy. */
  productV3Key: z.string().nullable(),
  /** Legacy v2 passenger, echoed for the same reason. */
  productId: z.string().uuid().nullable(),
  /**
   * NULL on every v3-era policy (ProductV3 has no variants). This was a required
   * uuid, and the service filtered null-variant rows out to satisfy it — which
   * silently dropped every v3 policy and made this endpoint return zero rows.
   */
  productVariantId: z.string().uuid().nullable(),
  variantLabel: z.string().nullable(),
  minQuantity: z.number(),
  maxQuantity: z.number(),
  autoTransferEligible: z.boolean(),
  autoBuyEligible: z.boolean(),
  baseMinQuantity: z.number(),
  baseMaxQuantity: z.number(),
  baseAutoTransferEligible: z.boolean(),
  baseAutoBuyEligible: z.boolean(),
  safetyStock: z.number().nullable(),
  reorderPoint: z.number().nullable(),
  orderUpToLevel: z.number().nullable(),
  activeOverrideCount: z.number(),
});

export type PolicyOverrideComboRow = z.infer<
  typeof PolicyOverrideComboRowSchema
>;

export const ListPolicyOverrideCombosInputSchema = z
  .object({
    /** ProductV3 key (the SKU). Preferred — reaches v3-only policies. */
    productV3Key: z.string().min(1).nullable().optional(),
    /** Legacy v2 product id. Still accepted for pre-re-key policies. */
    productId: z.string().uuid().nullable().optional(),
  })
  .refine(atLeastOneProductKey.check, {
    message: atLeastOneProductKey.message,
  });

export const ListPolicyOverrideCombosResponseSchema = z.object({
  rows: z.array(PolicyOverrideComboRowSchema),
});

export type ListPolicyOverrideCombosInput = z.infer<
  typeof ListPolicyOverrideCombosInputSchema
>;
export type ListPolicyOverrideCombosResponse = z.infer<
  typeof ListPolicyOverrideCombosResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeInventoryPolicyOverrideInputSchema = z.object({
  overrideId: z.string().uuid(),
});

export const RevokeInventoryPolicyOverrideResponseSchema = z.object({
  override: InventoryPolicyOverrideRowSchema,
});

export type RevokeInventoryPolicyOverrideInput = z.infer<
  typeof RevokeInventoryPolicyOverrideInputSchema
>;
export type RevokeInventoryPolicyOverrideResponse = z.infer<
  typeof RevokeInventoryPolicyOverrideResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const inventoryPolicyOverridesContracts = {
  create: oc
    .input(CreateInventoryPolicyOverrideInputSchema)
    .output(CreateInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary:
        "Create a time-gated override pinning inventory-policy fields (min/max/auto-eligibility) over a window.",
    }),
  listByPolicy: oc
    .input(ListInventoryPolicyOverridesInputSchema)
    .output(ListInventoryPolicyOverridesResponseSchema)
    .route({
      method: "GET",
      summary:
        "List all overrides (active + historical) for a policy, plus the resolved effective values.",
    }),
  listByProduct: oc
    .input(ListPolicyOverrideCombosInputSchema)
    .output(ListPolicyOverrideCombosResponseSchema)
    .route({
      method: "GET",
      summary:
        "List every inventory-policy combo (warehouse×variant) for a product with effective + base values, for the product-detail overrides table.",
    }),
  revoke: oc
    .input(RevokeInventoryPolicyOverrideInputSchema)
    .output(RevokeInventoryPolicyOverrideResponseSchema)
    .route({
      method: "POST",
      summary:
        "Revoke an override immediately (sets deactivated_at); any prior override resumes.",
    }),
} as const;
